import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { EmptyState } from "../../../common/comms/EmptyState";
import { formatSecondsBrief, formatDuration, pct } from "../../../utils/oversightFormat";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const ORANGE = "#FF6414";
const GRAY = "#6B7280";
const BLUE = "#3B82F6";
const GREEN = "#1D9E75";
const AXIS_COLOR = "#6B7280";
const GRID_COLOR = "rgba(226,232,240,0.6)";
const MAX_TICKS = 8;

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Format a `series[].date` (`YYYY-MM-DD…`) into a short x-axis label.
 * Day granularity → "Jul 9"; week granularity → ["Week 1", "Jul 7"], numbered
 * from the oldest bucket in the range, with the bucket's start date as a
 * second line (Chart.js renders an array label as stacked lines, and the
 * tooltip title shows both). Parsed manually (no `Date`) to avoid timezone
 * drift on date-only strings.
 * @param {string|null|undefined} dateStr - Bucket date, e.g. "2026-07-09".
 * @param {'day'|'week'} granularity - Bucket size.
 * @param {number} index - Position of the bucket in the series (0 = oldest).
 * @returns {string|Array<string>} Short label, or the raw value if unparsable.
 */
function formatDateLabel(dateStr, granularity, index) {
  if (!dateStr) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr));
  if (!match) return String(dateStr);
  const month = MONTH_ABBR[Number(match[2]) - 1] ?? match[2];
  const day = Number(match[3]);
  const short = `${month} ${day}`;
  return granularity === "week" ? [`Week ${index + 1}`, short] : short;
}

/** Coerce a metric field to a finite number, or `null` (chart gap) when absent/invalid. */
function numOrNull(value) {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Pull one metric out of every series row as a null-safe array (same order/length as `rows`). */
function seriesValues(rows, key) {
  return rows.map((row) => numOrNull(row?.[key]));
}

const legendPlugin = {
  position: "bottom",
  labels: { boxWidth: 8, boxHeight: 8, usePointStyle: true, padding: 12, font: { size: 11 } },
};

const xScale = {
  ticks: { maxTicksLimit: MAX_TICKS, font: { size: 10 }, color: AXIS_COLOR },
  grid: { display: false },
};

/** Chart panel shell: title, white card, and a horizontally-scrollable fixed-height canvas area. */
function ChartPanel({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-sm font-semibold text-gray-700 mb-3">{title}</p>
      <div className="overflow-x-auto">
        <div className="min-w-[560px] h-56">{children}</div>
      </div>
    </div>
  );
}

/** Activity panel: messages sent + sessions count. */
function buildActivityChart(rows, labels) {
  const data = {
    labels,
    datasets: [
      {
        label: "Messages sent",
        data: seriesValues(rows, "messages_sent"),
        borderColor: ORANGE,
        backgroundColor: ORANGE,
        pointBackgroundColor: ORANGE,
        borderWidth: 2,
        pointRadius: 2,
        spanGaps: true,
        tension: 0.25,
      },
      {
        label: "Sessions",
        data: seriesValues(rows, "sessions_count"),
        borderColor: GRAY,
        backgroundColor: GRAY,
        pointBackgroundColor: GRAY,
        borderWidth: 2,
        pointRadius: 2,
        spanGaps: true,
        tension: 0.25,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: { legend: legendPlugin },
    scales: {
      x: xScale,
      y: {
        beginAtZero: true,
        ticks: { font: { size: 10 }, color: AXIS_COLOR, precision: 0 },
        grid: { color: GRID_COLOR },
      },
    },
  };

  return { data, options };
}

/** Responsiveness panel: median + p90 first-response time, formatted via `formatSecondsBrief`. */
function buildResponsivenessChart(rows, labels) {
  const data = {
    labels,
    datasets: [
      {
        label: "Median first response",
        data: seriesValues(rows, "median_first_response_seconds"),
        borderColor: ORANGE,
        backgroundColor: ORANGE,
        pointBackgroundColor: ORANGE,
        borderWidth: 2,
        pointRadius: 2,
        spanGaps: true,
        tension: 0.25,
      },
      {
        label: "P90 first response",
        data: seriesValues(rows, "p90_first_response_seconds"),
        borderColor: BLUE,
        backgroundColor: BLUE,
        pointBackgroundColor: BLUE,
        borderWidth: 2,
        pointRadius: 2,
        spanGaps: true,
        tension: 0.25,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: legendPlugin,
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${formatSecondsBrief(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: xScale,
      y: {
        beginAtZero: true,
        ticks: {
          font: { size: 10 },
          color: AXIS_COLOR,
          callback: (value) => formatSecondsBrief(value),
        },
        grid: { color: GRID_COLOR },
      },
    },
  };

  return { data, options };
}

/** Quality panel: SLA hit rate (0-1, left axis, %) + CSAT average (0-5, right axis). */
function buildQualityChart(rows, labels) {
  const data = {
    labels,
    datasets: [
      {
        label: "SLA hit rate",
        data: seriesValues(rows, "sla_hit_rate"),
        borderColor: GREEN,
        backgroundColor: GREEN,
        pointBackgroundColor: GREEN,
        borderWidth: 2,
        pointRadius: 2,
        spanGaps: true,
        tension: 0.25,
        yAxisID: "y",
      },
      {
        label: "CSAT avg",
        data: seriesValues(rows, "csat_avg"),
        borderColor: BLUE,
        backgroundColor: BLUE,
        pointBackgroundColor: BLUE,
        borderWidth: 2,
        pointRadius: 2,
        spanGaps: true,
        tension: 0.25,
        yAxisID: "y1",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: legendPlugin,
      tooltip: {
        callbacks: {
          label: (ctx) =>
            ctx.dataset.yAxisID === "y1"
              ? ` ${ctx.dataset.label}: ${ctx.parsed.y == null ? "—" : ctx.parsed.y}`
              : ` ${ctx.dataset.label}: ${pct(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: xScale,
      y: {
        type: "linear",
        position: "left",
        min: 0,
        max: 1,
        ticks: { font: { size: 10 }, color: AXIS_COLOR, callback: (value) => pct(value) },
        grid: { color: GRID_COLOR },
      },
      y1: {
        type: "linear",
        position: "right",
        min: 0,
        max: 5,
        ticks: { font: { size: 10 }, color: AXIS_COLOR },
        grid: { drawOnChartArea: false },
      },
    },
  };

  return { data, options };
}

/** Online hours panel: `online_seconds` rendered in hours (1 decimal); tooltip shows `formatDuration`. */
function buildOnlineHoursChart(rows, labels) {
  const hoursValues = rows.map((row) => {
    const secs = numOrNull(row?.online_seconds);
    return secs == null ? null : Math.round((secs / 3600) * 10) / 10;
  });

  const data = {
    labels,
    datasets: [
      {
        label: "Online hours",
        data: hoursValues,
        borderColor: ORANGE,
        backgroundColor: ORANGE,
        pointBackgroundColor: ORANGE,
        borderWidth: 2,
        pointRadius: 2,
        spanGaps: true,
        tension: 0.25,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: legendPlugin,
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${formatDuration(rows[ctx.dataIndex]?.online_seconds)}`,
        },
      },
    },
    scales: {
      x: xScale,
      y: {
        beginAtZero: true,
        ticks: { font: { size: 10 }, color: AXIS_COLOR },
        grid: { color: GRID_COLOR },
      },
    },
  };

  return { data, options };
}

/**
 * Trend chart panel for a coach's per-day/per-week analytics series.
 *
 * Renders four `react-chartjs-2` `Line` charts over `series`: Activity
 * (messages sent + sessions), Responsiveness (median/p90 first-response
 * time), Quality (SLA hit rate % + CSAT avg on a secondary axis), and
 * Online hours. Metric values are null-safe (`spanGaps`) so missing/`null`
 * rollup fields render as gaps rather than crashing. X-axis labels adapt to
 * `granularity` (day vs. week) via `formatDateLabel`. Each chart lives in a
 * fixed-height, horizontally-scrollable wrapper so the panel — not the page
 * — scrolls on narrow viewports.
 *
 * @param {Object} props
 * @param {Array<Object>|null|undefined} props.series - Per-`granularity` metric points (`{ date, … }`).
 * @param {'day'|'week'} props.granularity - Bucket size the series is grouped by.
 */
export function CoachTrendCharts({ series, granularity }) {
  const rows = Array.isArray(series) ? series : [];

  const charts = useMemo(() => {
    if (rows.length === 0) return null;
    const labels = rows.map((row, index) => formatDateLabel(row?.date, granularity, index));
    return {
      activity: buildActivityChart(rows, labels),
      responsiveness: buildResponsivenessChart(rows, labels),
      quality: buildQualityChart(rows, labels),
      onlineHours: buildOnlineHoursChart(rows, labels),
    };
  }, [rows, granularity]);

  if (!charts) {
    return <EmptyState title="No activity in this period" />;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ChartPanel title="Activity">
        <Line data={charts.activity.data} options={charts.activity.options} />
      </ChartPanel>
      <ChartPanel title="Responsiveness">
        <Line data={charts.responsiveness.data} options={charts.responsiveness.options} />
      </ChartPanel>
      <ChartPanel title="Quality">
        <Line data={charts.quality.data} options={charts.quality.options} />
      </ChartPanel>
      <ChartPanel title="Online hours">
        <Line data={charts.onlineHours.data} options={charts.onlineHours.options} />
      </ChartPanel>
    </div>
  );
}
